// The Stewards' city as a list of things to draw, in the same world units and
// with the same Kenney models the table uses. Pure, so it can be unit-tested
// without a GPU: `layoutCity(civ)` -> tiles, buildings, slots, spots.
//
// Matt: "I want it to look like the map on the TV... except zoomed way in so
// that I can see the buildings that are getting added to it. And then we
// could also use those building assets."

import {
  BUILDINGS, cityOf, annexable, workable, roomIn, PLOTS_PER_HEX, slotsFor } from '@/engine/city';
import { hexToWorld, TILE_TOP } from './layout';

export { hexToWorld };

// One Kenney model per kind of building in the city.
export const CITY_MODELS = {
  field: 'building-farm',
  workshop: 'building-smelter',
  market: 'building-market',
  granary: 'building-cabin',
  warehouse: 'building-house',
  mill: 'building-mill',
  house: 'building-village',
  // The Scholars' machines (2026-10-15).
  kiln: 'building-mine',
  still: 'building-watermill',
  crucible: 'building-wizard-tower',
};

export const GROUND = 'grass';
// The square must stand at the SAME height as the rest of the city: `dirt` is
// a shorter tile, and a short tile beside a tall one means the taller
// neighbour hides whatever stands near the short one's edge. Matt: "the green
// fields are twice as thick as the red hexes... your pad on the red hexes is
// covered by the green field."
export const SQUARE_GROUND = 'sand';

// SIX TRIANGLES, not six little hexes. Matt: "we can't fit six buildings into
// this hexagon. Hexagons don't stack into hexagons... we can do six triangles.
// We could do two trapezoids. We could do three diamonds." A hexagon divides
// exactly into six equilateral triangles from its middle, which is also
// exactly PLOTS_PER_HEX, and a two-plot building is two of them: a rhombus.
const R = 0.577; // the hex's corner radius, matching layout.js HEX_R

const corner = (i) => {
  const a = (Math.PI / 180) * (60 * i - 90);
  return { x: R * Math.cos(a), z: R * Math.sin(a) };
};

/** The three points of plot i: the middle of the hex and two of its corners. */
export const plotCorners = (i) => [{ x: 0, z: 0 }, corner(i), corner((i + 1) % 6)];

/** The middle of plot i - where its building stands. */
export const plotAt = (i) => {
  const [a, b, c] = plotCorners(i);
  return { dx: (a.x + b.x + c.x) / 3, dz: (a.z + b.z + c.z) / 3 };
};

/** Straight out from the middle of the hex through plot i. */
export const plotOut = (i) => {
  const a = (Math.PI / 180) * (60 * i - 60);
  return { x: Math.cos(a), z: Math.sin(a) };
};

// Where a worker stands: BESIDE the building, inside its own plot. Pushing
// them outward put them in the neighbouring triangle; pushing them toward the
// camera put them off the plot entirely. Sideways is the only direction that
// stays home, and they are small enough to fit there.
const BESIDE = 0.13;
const beside = (dirs, toward) => {
  const x = dirs.reduce((n, d) => n + d.x, 0) / dirs.length;
  const z = dirs.reduce((n, d) => n + d.z, 0) / dirs.length;
  const len = Math.hypot(x, z) || 1;
  // perpendicular to the plot's own line, on whichever side faces the camera
  let px = -(z / len);
  let pz = x / len;
  if (px * toward.x + pz * toward.z < 0) { px = -px; pz = -pz; }
  return { dx: px * BESIDE, dz: pz * BESIDE };
};

export const MODELS_FOR_CITY = [...new Set([
  GROUND, SQUARE_GROUND, ...Object.values(CITY_MODELS),
])];

/**
 * Every building in the city with where it stands and what it is worth,
 * plus the empty plots, the land you could take in, and the spots off the rim.
 */
// Which way the camera looks, flattened onto the ground. cityWorld.js uses
// the same fixed angle; a worker put on this side of a building is the one
// you can see.
const CAMERA_DIR = (() => {
  const x = 0.9;
  const z = 1.15;
  const len = Math.hypot(x, z);
  return { x: x / len, z: z / len };
})();

export const layoutCity = (civ, yields = {}, toward = CAMERA_DIR) => {
  const city = cityOf(civ);
  const can = new Set(workable(civ).map((b) => b.id));
  const tiles = [];
  const buildings = [];
  const plots = [];

  city.hexes.forEach(([q, r]) => {
    const centre = q === 0 && r === 0;
    const model = centre ? SQUARE_GROUND : GROUND;
    tiles.push({
      id: `t${q},${r}`, model, q, r, y: 0, kind: 'hex', centre, room: roomIn(city, [q, r]),
    });
    const taken = [];
    let plot = 0;
    city.buildings
      .filter((b) => b.hex[0] === q && b.hex[1] === r)
      .forEach((b) => {
        const spec = BUILDINGS[b.type];
        const wide = spec.plots === 2;
        if (wide && plot % 2 === 1) plot += 1;
        // Round the hex in order, but never onto a plot already built on: a
        // wide building that skipped an odd plot used to push the last one
        // round past six and on top of the first (playtest nOo8q - a
        // workshop drawn over a field). The skipped plot is still free.
        const free = (i) => !taken.includes(i % PLOTS_PER_HEX);
        const fits = (i) => free(i) && (!wide || free(i + 1));
        if (plot >= PLOTS_PER_HEX || !fits(plot)) {
          const order = wide ? [0, 2, 4, 1, 3, 5] : [0, 1, 2, 3, 4, 5];
          const found = order.find(fits);
          if (found !== undefined) plot = found;
        }
        const mine = [];
        for (let i = 0; i < spec.plots; i += 1) mine.push((plot + i) % PLOTS_PER_HEX);
        const off = {
          dx: mine.reduce((n, i) => n + plotAt(i).dx, 0) / mine.length,
          dz: mine.reduce((n, i) => n + plotAt(i).dz, 0) / mine.length,
        };
        mine.forEach((i) => taken.push(i));
        plot += spec.plots;
        buildings.push({
          id: b.id,
          kind: 'building',
          model: CITY_MODELS[b.type] || CITY_MODELS.workshop,
          type: b.type,
          q,
          r,
          ...off,
          y: TILE_TOP[model] ?? 0.2,
          // Small enough that the worker's pad in front of it is not buried
          // inside the model.
          // Six buildings share one hex, so each is small; the camera is
          // close enough that they still read.
          scale: wide ? 0.4 : 0.3,
          plots: mine,
          // where the workers stand, beside the building in its own plot
          ...(() => {
            const at = beside(mine.map(plotOut), toward);
            return { standDx: off.dx + at.dx, standDz: off.dz + at.dz };
          })(),
          burned: Boolean(b.burned),
          staffed: b.staffed,
          slots: slotsFor(civ, b.type),
          workable: can.has(b.id),
          yield: yields[b.type] ?? null,
        });
      });
    // Every plot is drawn, taken or not: the six spaces ARE the board.
    for (let i = 0; i < PLOTS_PER_HEX; i += 1) {
      plots.push({
        id: `p${q},${r},${i}`,
        kind: 'plot',
        q,
        r,
        index: i,
        free: !taken.includes(i),
        corners: plotCorners(i),
        ...plotAt(i),
        y: TILE_TOP[model] ?? 0.2,
      });
    }
  });

  const annex = annexable(civ).map(([q, r]) => ({
    id: `a${q},${r}`, kind: 'annex', q, r, y: 0.02,
  }));

  return {
    tiles, buildings, plots, annex,
  };
};
